/**
 * แผลเปิดช้า (the delayed wound, status `woundPct`, kit audit 2026-10-13 piece 11 one-cut): a stack put on starts (or
 * restarts) the status's timer; when the timer runs out on its own the wound opens: the bearer loses `woundPct` × stacks of
 * max HP at once (never the killing blow, as poison's burst). A cleanse or a cash-out that takes the stacks off early opens
 * nothing. Watches the `status` events (a new stack, the status gone) and keeps its own clock of each wound, so it does not
 * depend on where the World's status tick falls in the step. Deterministic: no draws, time from `step(dt)`.
 */
import { STATUSES } from '../status';
import type { WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** A status gone this close to its timer's end counts as run out (the World's tick and this clock may be a step apart). */
const SLACK = 2 / 60 + 1e-9;

const SYSTEMS = new WeakMap<object, LateWoundSystem>();
export const lateWoundOf = (w: object): LateWoundSystem | null => SYSTEMS.get(w) ?? null;

export class LateWoundSystem implements WorldSystem {
  readonly name = 'lateWound';
  /** `entity id:status` -> stacks and seconds left on the wound's clock. */
  private readonly wounds = new Map<string, { id: number; status: string; stacks: number; left: number }>();
  /** Counts for the tests: wounds opened, and wounds lifted early. */
  readonly tally = { opened: 0, lifted: 0 };

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  observe(ev: WorldEvent): void {
    if (ev.type !== 'status') return;
    const def = STATUSES[ev.status];
    if (!def?.woundPct) return;
    const key = `${ev.id}:${ev.status}`;
    if (ev.stacks > 0) {
      this.wounds.set(key, { id: ev.id, status: ev.status, stacks: ev.stacks, left: def.duration });
      return;
    }
    const wd = this.wounds.get(key);
    if (!wd) return;
    if (wd.left <= SLACK) this.open(key);
    else {
      this.wounds.delete(key);
      this.tally.lifted++;
    }
  }

  private open(key: string): void {
    const wd = this.wounds.get(key);
    this.wounds.delete(key);
    if (!wd) return;
    const e = this.h.world.get(wd.id);
    const pct = STATUSES[wd.status]?.woundPct ?? 0;
    if (!e || e.dead || pct <= 0) return;
    e.hp = Math.max(1, e.hp - Math.round(e.maxHp * pct * wd.stacks));
    this.tally.opened++;
  }

  step(dt: number): void {
    for (const [key, wd] of [...this.wounds]) {
      wd.left -= dt;
      if (wd.left > 1e-9) continue;
      // Run out: open it now if the World's tick has not taken the status off yet (it will, and that event finds nothing).
      // Gone already with no event seen: it was taken off quietly (a safe zone sheds other players' harm): nothing opens.
      const e = this.h.world.get(wd.id);
      if (e && !e.dead && e.statuses.some((s) => s.id === wd.status)) this.open(key);
      else {
        this.wounds.delete(key);
        this.tally.lifted++;
      }
    }
  }
}
